import React from 'react'
import { Stack } from './Stack'
import { Text } from './Text'
import { RadioInput } from './RadioInput'
import { useBreakpoints, ResponsiveValue } from './hooks/useBreakpoints'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { Inline } from './Inline'

interface RadioOption {
	value: string
	label: string
	disabled?: boolean
}

interface RadioGroupProps<T extends FieldValues> {
	label: string
	options: RadioOption[]
	value?: string
	onChange?: (value: string) => void
	direction?: 'horizontal' | 'vertical'
	align?: ResponsiveValue<'center' | 'start'>
	control?: Control<T>
	name?: Path<T>
}

export function RadioGroup<T extends FieldValues>({
	label,
	value,
	onChange,
	options,
	direction = 'vertical',
	align,
	control,
	name,
}: RadioGroupProps<T>) {
	const { value: getResponsiveValue } = useBreakpoints()

	const currentAlign = align ? getResponsiveValue(align) : 'start'
	const stackAlign = currentAlign === 'center' ? 'center' : 'left'

	const renderOptions = (selectedValue: string, onChangeHandler: (value: string) => void) => {
		if (direction === 'horizontal') {
			return (
				<Inline
					gap="md"
					alignX={{
						mobile: currentAlign === 'center' ? 'center' : 'left',
						tablet: currentAlign === 'center' ? 'center' : 'left',
						desktop: currentAlign === 'center' ? 'center' : 'left',
					}}
				>
					{options.map(option => (
						<RadioInput
							key={option.value}
							label={option.label}
							selected={selectedValue === option.value}
							onPress={() => onChangeHandler(option.value)}
							disabled={option.disabled}
							value={option.value}
							name={name}
						/>
					))}
				</Inline>
			)
		}

		return (
			<Stack
				gap="md"
				style={{
					flexDirection: 'column',
					justifyContent: currentAlign === 'center' ? 'center' : 'flex-start',
				}}
			>
				{options.map(option => (
					<RadioInput
						key={option.value}
						label={option.label}
						selected={selectedValue === option.value}
						onPress={() => onChangeHandler(option.value)}
						disabled={option.disabled}
						value={option.value}
						name={name}
					/>
				))}
			</Stack>
		)
	}

	const renderRadioGroup = () => renderOptions(value || '', onChange || (() => {}))

	return (
		<Stack gap="md" align={stackAlign}>
			<Text level="body" weight="strong">
				{label}
			</Text>
			{control && name ? (
				<Controller
					control={control}
					name={name}
					render={({
						field: { onChange: fieldOnChange, value: fieldValue },
						fieldState: { error },
					}) => {
						return (
							<Stack gap="sm">
								{renderOptions(fieldValue, fieldOnChange)}
								{error?.message && (
									<Text level="small" tone="critical">
										{error.message}
									</Text>
								)}
							</Stack>
						)
					}}
				/>
			) : (
				renderRadioGroup()
			)}
		</Stack>
	)
}
